import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";

import { IConfigCreateRequest } from "../../../../../models";
import { createRequestAction } from "../../../../../redux/actions/request/create/createRequest";
import { useAppDispatch, useAppSelector } from "../../../../../redux/hooks";
import { nextStep, previousStep } from "../../../../../redux/reducers/request/create/createRequest";
import { throwErrorToastrAsync } from "../../../../../utils";
import { ButtonPrimary, ButtonPrimaryInverseWithBorder } from "../../../../common/buttons/buttons";
import { PopUp } from "../../../../common/popup/popUp";
import { RequestButtonsStyle } from "./requestButtons.jss";
import { SummaryMessage } from "./summaryMessage/summaryMessage";
import { OrganismTypes, RoleType, OrganizationTypes, RepresentativePresenceTypes } from "../../../../../common/enum";

interface IProps {
    isValid: boolean;
    isLastStep: boolean;
    isFirstStep: boolean;
    showSummaryMessage?: boolean;
    schema: IConfigCreateRequest;
}

export const RequestButtons: React.FC<IProps> = ({ isValid, isFirstStep, isLastStep, showSummaryMessage, schema }) => {
    const theme = useTheme();
    const styles = RequestButtonsStyle({ theme });
    const dispatch = useAppDispatch();
    const [showSummaryPopUp, setShowSummaryPopUp] = useState(false);
    const { t } = useTranslation("common");
    const { request } = useAppSelector((store) => store.createRequest);
    const representativePresenceTypes = useAppSelector((store) => store.catalog.representativePresenceTypes.data);
    const representativePresenceType = representativePresenceTypes.find((fd) => fd.id === request.representativePresenceTypeId);
    const organims = useAppSelector((store) => store.catalog.organisms.data);
    const organism = organims.find((org) => org.code === OrganismTypes.CUSTOMS);
    const inspections = request.inspections.filter((ins) => ins.organismId === organism?.id);
    const currentOrganization = useAppSelector((store) => store.organization.organization.id);
    const currentUserRole = useAppSelector((store) => store.role.roleData.id);

    const onSave = useCallback(() => {
        if (
            (currentUserRole === RoleType.CUSTOMER_ADMIN || currentUserRole === RoleType.CUSTOMER_USER) &&
            currentOrganization === OrganizationTypes.TNF &&
            representativePresenceType?.code === RepresentativePresenceTypes.NO &&
            inspections.length > 0
        ) {
            const error: Error = {
                message: t("CREATE_REQUEST.INFO.CUSTOMS_MESSAGE"),
                name: "",
            };
            return throwErrorToastrAsync(error);
        }

        dispatch(createRequestAction());
    }, [dispatch, representativePresenceType, inspections]);

    const onClickPrevious = useCallback(() => {
        dispatch(previousStep());
    }, [dispatch]);

    const onClickNext = useCallback(() => {
        if (showSummaryMessage) {
            setShowSummaryPopUp(true);
        } else {
            dispatch(nextStep());
        }
    }, [dispatch, showSummaryMessage]);

    const onClosePopUp = () => {
        setShowSummaryPopUp(false);
    };

    const onAccept = () => {
        setShowSummaryPopUp(false);
        dispatch(nextStep());
    };

    return (
        <>
            <PopUp
                children={
                    <SummaryMessage
                        onAccept={onAccept}
                        onCancel={onClosePopUp}
                    />
                }
                state={showSummaryPopUp}
                hideModal={onClosePopUp}
                isBlocking={true}
                title={t("CREATE_REQUEST.TITLES.SUMMARY_TITLE")}
            />

            <div className={styles.requestButtonsContainer}>
                <div className={styles.disclaimer}>
                    <span>* </span> {t("CREATE_REQUEST.FIELDS.REQUIRED_FIELDS")}
                </div>
                <div className={styles.buttons}>
                    {!isFirstStep && (
                        <ButtonPrimaryInverseWithBorder
                            handleClick={onClickPrevious}
                            title={t("CREATE_REQUEST.BUTTONS.BACK")}
                        />
                    )}
                    {!isLastStep && (
                        <ButtonPrimary
                            disabled={!isValid}
                            handleClick={onClickNext}
                            title={t("CREATE_REQUEST.BUTTONS.NEXT")}
                        />
                    )}
                    {isLastStep && (
                        <ButtonPrimary
                            disabled={!isValid}
                            handleClick={onSave}
                            title={t("CREATE_REQUEST.BUTTONS.SAVE")}
                        />
                    )}
                </div>
            </div>
        </>
    );
};
